import { useState } from 'react';
import { format, isAfter, isSameDay, startOfDay } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { DateRangePicker as ReactDateRangePicker, type RangeKeyDict } from 'react-date-range';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import './date-range.css';

interface DateRangeValue {
	startDate?: Date;
	endDate?: Date;
}

interface DateRangePickerProps {
	value?: DateRangeValue;
	onChange?: (range: DateRangeValue) => void;
	disabled?: (date: Date) => boolean;
	placeholder?: { start?: string; end?: string };
	className?: string;
}

/** The Sentinel Analytics date pill: pick a range in one month, then Apply; future days can't be picked. */
export function DateRangePicker({ value, onChange, disabled, placeholder = { start: 'Start Date', end: 'End Date' }, className }: DateRangePickerProps) {
	const [open, setOpen] = useState(false);
	const [draft, setDraft] = useState({ startDate: new Date(), endDate: new Date() });
	const label =
		value?.startDate && value.endDate
			? isSameDay(value.startDate, value.endDate)
				? format(value.startDate, 'MMM d, yyyy')
				: `${format(value.startDate, 'MMM d, yyyy')} - ${format(value.endDate, 'MMM d, yyyy')}`
			: `${placeholder.start} - ${placeholder.end}`;
	const selectRange = (ranges: RangeKeyDict) => {
		const range = ranges.selection;
		if (range?.startDate && range.endDate) setDraft({ startDate: range.startDate, endDate: range.endDate });
	};
	const openChange = (next: boolean) => {
		if (next) setDraft({ startDate: value?.startDate ?? new Date(), endDate: value?.endDate ?? value?.startDate ?? new Date() });
		setOpen(next);
	};
	return (
		<Popover open={open} onOpenChange={openChange}>
			<PopoverTrigger asChild>
				<button
					type='button'
					aria-label='Date range'
					className={cn(
						'flex h-8 w-[230px] items-center gap-2 rounded-full border border-gray-300 bg-white px-3 text-xs text-gray-800 focus-visible:outline-2 focus-visible:outline-offset-2',
						!value?.startDate && 'text-muted-foreground',
						className
					)}
				>
					<CalendarIcon aria-hidden='true' className='size-3.5 shrink-0' />
					<span className='min-w-0 flex-1 whitespace-nowrap text-center'>{label}</span>
				</button>
			</PopoverTrigger>
			<PopoverContent align='end' sideOffset={8} collisionPadding={16} className='w-auto max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg p-0'>
				<div className='max-h-[min(480px,calc(var(--radix-popover-content-available-height)-64px))] overflow-auto [&_.rdrDateRangePickerWrapper]:flex-wrap [&_.rdrDefinedRangesWrapper]:shrink-0 [&_.rdrMonth]:max-w-full [&_.rdrStaticRange]:rounded-[4px] max-sm:[&_.rdrDefinedRangesWrapper]:w-full'>
					<ReactDateRangePicker
						ranges={[{ ...draft, key: 'selection' }]}
						onChange={selectRange}
						months={1}
						direction='horizontal'
						maxDate={new Date()}
						disabledDay={(date) => isAfter(startOfDay(date), startOfDay(new Date())) || (disabled?.(date) ?? false)}
						moveRangeOnFirstSelection={false}
						editableDateInputs
						inputRanges={[]}
					/>
				</div>
				<div className='flex justify-end gap-2 border-t border-gray-200 p-3'>
					<Button type='button' variant='outline' size='sm' onClick={() => setOpen(false)}>Cancel</Button>
					<Button type='button' size='sm' onClick={() => { onChange?.(draft); setOpen(false); }}>Apply</Button>
				</div>
			</PopoverContent>
		</Popover>
	);
}
